import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Download,
  Mail,
  Briefcase,
  Calendar,
  FileText,
  Video,
  TrendingUp,
  MessageSquare,
} from 'lucide-react';
import CircularGauge from '@/components/CircularGauge';

interface CandidateDetail {
  id: string;
  fullName: string;
  email: string;
  bio?: string;
  resumeUrl?: string;
  jobTitle: string;
  jobDescription: string;
  interviewDate: string;
  duration?: string;
  finalScore: number | null;
  communicationScore: number | null;
  technicalScore: number | null;
  confidenceScore: number | null;
  transcript?: string;
  videoUrl?: string;
  evaluation?: string;
  status: string;
}

export default function CandidateDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { getOrganizationId, isRecruiter, isRecruiterValue, organizationId, userProfile } = useAuth();
  const [candidate, setCandidate] = useState<CandidateDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userProfile && !isRecruiterValue) {
      navigate('/dashboard');
      return;
    }

    if (!userProfile) {
      return;
    }

    const fetchCandidateDetail = async () => {
      if (!organizationId || !sessionId) {
        setLoading(false);
        return;
      }

      try {
        const { data: sessionData, error: sessionError } = await supabase
          .from('interview_sessions')
          .select(`
            *,
            job_postings (
              id,
              title,
              description,
              organization_id
            ),
            user_profiles (
              full_name,
              bio,
              resume_url
            )
          `)
          .eq('id', sessionId)
          .maybeSingle();

        if (sessionError) throw sessionError;

        if (!sessionData) {
          console.error('Interview session not found');
          navigate('/candidates');
          return;
        }

        const jobPosting = sessionData.job_postings as any;
        if (jobPosting?.organization_id !== organizationId) {
          console.error('Unauthorized access');
          navigate('/candidates');
          return;
        }

        let userEmail = 'Anonymous';
        if (sessionData.user_id) {
          const { data: { user }, error: userError } = await supabase.auth.admin.getUserById(sessionData.user_id);
          if (!userError && user) {
            userEmail = user.email || 'N/A';
          }
        }

        const userProfile = sessionData.user_profiles as any;

        const detail: CandidateDetail = {
          id: sessionData.user_id || sessionData.id,
          fullName: userProfile?.full_name || 'Anonymous',
          email: userEmail,
          bio: userProfile?.bio,
          resumeUrl: userProfile?.resume_url,
          jobTitle: jobPosting?.title || 'Unknown Job',
          jobDescription: jobPosting?.description || '',
          interviewDate: sessionData.created_at,
          duration: sessionData.duration,
          finalScore: sessionData.final_score,
          communicationScore: sessionData.communication_score,
          technicalScore: sessionData.technical_score,
          confidenceScore: sessionData.confidence_score,
          transcript: sessionData.transcript,
          videoUrl: sessionData.video_url,
          evaluation: sessionData.evaluation,
          status: sessionData.final_score !== null ? 'completed' : 'in-progress',
        };

        setCandidate(detail);
      } catch (error) {
        console.error('Error fetching candidate detail:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateDetail();
  }, [sessionId, getOrganizationId, isRecruiter, navigate, isRecruiterValue, organizationId, userProfile]);

  const handleDownloadTranscript = () => {
    if (!candidate?.transcript) return;

    const blob = new Blob([candidate.transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${candidate.fullName.replace(/\s+/g, '_')}_transcript.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Candidate not found</p>
          <Button onClick={() => navigate('/candidates')} className="mt-4">
            Back to Candidates
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Button onClick={() => navigate('/candidates')} variant="ghost" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Candidates
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{candidate.fullName}</h1>
              <p className="text-gray-600 mt-1">{candidate.jobTitle}</p>
            </div>
            <Badge
              className={
                candidate.status === 'completed'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }
            >
              {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Candidate Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{candidate.email}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Applied For</p>
                  <p className="font-medium">{candidate.jobTitle}</p>
                </div>
              </div>

              <Separator />

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Interview Date</p>
                  <p className="font-medium">
                    {new Date(candidate.interviewDate).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              {candidate.bio && (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Bio</p>
                    <p className="text-gray-900">{candidate.bio}</p>
                  </div>
                </>
              )}

              {candidate.resumeUrl && (
                <>
                  <Separator />
                  <Button onClick={() => window.open(candidate.resumeUrl, '_blank')} variant="outline">
                    <FileText className="w-4 h-4 mr-2" />
                    View Resume
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Interview Scores</CardTitle>
            </CardHeader>
            <CardContent>
              {candidate.finalScore !== null ? (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <CircularGauge value={candidate.finalScore} size={120} strokeWidth={12} />
                  </div>

                  <div className="space-y-3">
                    {candidate.communicationScore !== null && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Communication</span>
                          <span className="text-sm font-semibold">
                            {candidate.communicationScore.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${candidate.communicationScore}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {candidate.technicalScore !== null && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Technical Knowledge</span>
                          <span className="text-sm font-semibold">
                            {candidate.technicalScore.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full"
                            style={{ width: `${candidate.technicalScore}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {candidate.confidenceScore !== null && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">Confidence</span>
                          <span className="text-sm font-semibold">
                            {candidate.confidenceScore.toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full"
                            style={{ width: `${candidate.confidenceScore}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">Interview not yet completed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {candidate.evaluation && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>AI Evaluation</CardTitle>
              <CardDescription>Detailed assessment from our AI interviewer</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-900 whitespace-pre-wrap">{candidate.evaluation}</p>
            </CardContent>
          </Card>
        )}

        {candidate.transcript && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Interview Transcript</CardTitle>
                  <CardDescription>Full conversation from the interview session</CardDescription>
                </div>
                <Button onClick={handleDownloadTranscript} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-6 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
                  {candidate.transcript}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {!candidate.transcript && candidate.status === 'in-progress' && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Interview transcript will appear once the interview is completed</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
